import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, Button, Input, Select, Textarea, Field, Stat, StatusBadge } from "@/components/UI";
import { Plus, Trash, Coins, Trophy, ChartLine, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";


export function PerformanceScorecard() {
  const { user } = useAuth();
  const canSeeCosts = !!user?.can_see_costs;
  const [data, setData] = useState(null);
  const [days, setDays] = useState(90);
  useEffect(() => {
    api.get(`/reports/vet-performance?days=${days}`).then(r => setData(r.data)).catch(() => {});
  }, [days]);
  if (!data) return <div className="text-[#8a938c]">Yükleniyor...</div>;

  const fmtPct = (v) => v == null ? "—" : `%${v.toFixed(1)}`;
  const pctTone = (v, kind = "high") => {
    // kind=high → yüksek iyi (başarı), kind=low → düşük iyi (nüks, ölü doğum)
    if (v == null) return { bg: "#EEECE5", fg: "#8a938c" };
    if (kind === "high") {
      if (v >= 70) return { bg: "#E4EEE6", fg: "#2D4A36" };
      if (v >= 40) return { bg: "#F7EBCC", fg: "#7a5a10" };
      return { bg: "#FBE9E4", fg: "#8a3a2b" };
    }
    if (v <= 10) return { bg: "#E4EEE6", fg: "#2D4A36" };
    if (v <= 25) return { bg: "#F7EBCC", fg: "#7a5a10" };
    return { bg: "#FBE9E4", fg: "#8a3a2b" };
  };
  const Chip = ({ value, kind = "high" }) => {
    const t = pctTone(value, kind);
    return <span className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold tabnum" style={{ background: t.bg, color: t.fg }}>{fmtPct(value)}</span>;
  };

  return (
    <div className="space-y-8" data-testid="perf-scorecard">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Trophy size={22} weight="duotone" className="text-[#DDA74F]" />
          <h3 className="heading text-2xl">Personel Karnesi</h3>
          <span className="text-xs text-[#8a938c]">· Son {days} gün</span>
        </div>
        <Select data-testid="perf-window" value={days} onChange={e => setDays(Number(e.target.value))} className="!w-auto">
          <option value={30}>Son 30 gün</option>
          <option value={90}>Son 90 gün</option>
          <option value={180}>Son 180 gün</option>
          <option value={365}>Son 1 yıl</option>
        </Select>
      </div>

      {/* Veterinerler */}
      <Card testId="perf-vets" className="!p-0 overflow-hidden">
        <div className="p-4 border-b border-[#EEECE5] flex items-center gap-2">
          <span className="text-lg">👨‍⚕️</span>
          <h4 className="font-semibold">Veterinerler ({data.veterinarians.length})</h4>
        </div>
        <table className="data-table">
          <thead>
            <tr>
              <th>Veteriner</th>
              <th className="text-right">Gebelik Kontrol</th>
              <th className="text-right">Gebelik Başarı</th>
              <th className="text-right">Tedavi</th>
              <th className="text-right">Tedavi Başarı</th>
              <th className="text-right">Nüks</th>
              <th className="text-right">Doğum</th>
              <th className="text-right">Güç/Sezaryen</th>
              {canSeeCosts && <th className="text-right">Toplam Gider</th>}
            </tr>
          </thead>
          <tbody>
            {data.veterinarians.map((v, i) => (
              <tr key={v.name + i} data-testid={`perf-vet-${i}`}>
                <td>
                  <div className="font-semibold">{v.name}</div>
                  <div className="text-[10px] text-[#8a938c] uppercase tracking-wider">veteriner</div>
                </td>
                <td className="text-right tabnum text-xs">
                  {v.preg_checks > 0 ? `${v.preg_checks} kontrol · ${v.preg_animals_checked} baş` : "—"}
                </td>
                <td className="text-right"><Chip value={v.preg_success_pct} kind="high" /></td>
                <td className="text-right tabnum text-xs">
                  {v.treatment_count > 0 ? (
                    <div>
                      <div>{v.treatment_count} tedavi</div>
                      <div className="text-[10px] text-[#8a938c]">✓ {v.treatment_healed} · ✗ {v.treatment_dead} · ⌛ {v.treatment_ongoing}</div>
                    </div>
                  ) : "—"}
                </td>
                <td className="text-right"><Chip value={v.treatment_success_pct} kind="high" /></td>
                <td className="text-right">
                  <Chip value={v.treatment_recurrence_pct} kind="low" />
                  {v.treatment_recurrence > 0 && (
                    <div className="text-[10px] text-[#8a938c] mt-0.5 tabnum">{v.treatment_recurrence} vaka</div>
                  )}
                </td>
                <td className="text-right tabnum text-xs">
                  {v.births_count > 0 ? `${v.births_count} · ${v.births_calves} yavru` : "—"}
                </td>
                <td className="text-right"><Chip value={v.birth_difficult_pct} kind="low" /></td>
                {canSeeCosts && <td className="text-right tabnum font-semibold">{Number(v.total_cost).toLocaleString("tr-TR")} ₺</td>}
              </tr>
            ))}
            {!data.veterinarians.length && (
              <tr><td colSpan={canSeeCosts ? 9 : 8} className="text-center py-8 text-[#8a938c]">Bu dönemde veteriner kaydı yok</td></tr>
            )}
          </tbody>
        </table>
        <div className="p-3 border-t border-[#EEECE5] text-[11px] text-[#5e675e] bg-[#FAF8F4]">
          <b>Nüks:</b> Aynı hayvan tipi + aynı teşhis 30 gün içinde tekrar açılan tedavi. Düşük olması iyidir.
          <b className="ml-3">Tedavi Başarı:</b> İyileşenler / (İyileşen + Vefat) — devam eden hariç.
        </div>
      </Card>

      {/* Tohumlayanlar */}
      <Card testId="perf-inseminators" className="!p-0 overflow-hidden">
        <div className="p-4 border-b border-[#EEECE5] flex items-center gap-2">
          <span className="text-lg">🐄</span>
          <h4 className="font-semibold">Tohumlayanlar ({data.inseminators.length})</h4>
        </div>
        <table className="data-table">
          <thead>
            <tr>
              <th>Tohumlayan</th>
              <th className="text-right">Kayıt Sayısı</th>
              <th className="text-right">Toplam Hayvan</th>
              <th>Yöntem</th>
              <th>Tohum Tipi</th>
            </tr>
          </thead>
          <tbody>
            {data.inseminators.map((t, i) => {
              const methodEntries = Object.entries(t.method_types || {});
              const semenEntries = Object.entries(t.semen_types || {});
              return (
                <tr key={t.name + i} data-testid={`perf-tech-${i}`}>
                  <td>
                    <div className="font-semibold">{t.name}</div>
                    <div className="text-[10px] text-[#8a938c] uppercase tracking-wider">tohumlayan</div>
                  </td>
                  <td className="text-right tabnum font-semibold">{t.insemination_count}</td>
                  <td className="text-right tabnum">{t.animal_count}</td>
                  <td className="text-xs">
                    {methodEntries.map(([k, v]) => (
                      <span key={k} className="inline-block mr-1 mb-0.5 px-1.5 py-0.5 rounded bg-[#F5F2EC] text-[#5e675e]">{k}: {v}</span>
                    ))}
                  </td>
                  <td className="text-xs">
                    {semenEntries.map(([k, v]) => (
                      <span key={k} className="inline-block mr-1 mb-0.5 px-1.5 py-0.5 rounded bg-[#DFEAE2] text-[#417251]">{k}: {v}</span>
                    ))}
                  </td>
                </tr>
              );
            })}
            {!data.inseminators.length && (
              <tr><td colSpan={5} className="text-center py-8 text-[#8a938c]">Bu dönemde tohumlama kaydı yok</td></tr>
            )}
          </tbody>
        </table>
        <div className="p-3 border-t border-[#EEECE5] text-[11px] text-[#5e675e] bg-[#FAF8F4]">
          Not: Tohumlama başarı oranı, gebelik kontrollerinden veteriner bazında hesaplanır (yukarıdaki tabloda "Gebelik Başarı" sütunu).
        </div>
      </Card>
    </div>
  );
}


